import { useEffect, useState } from "react";
import {
    getAllUsers,
    getuserById,
    getUserDevices,
    getUserStats,
} from "../service/api";
import { toast } from "sonner";
import {
    Eye,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    Smartphone,
    Globe,
    Wifi,
    MapPin,
    Clock,
    Activity,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

// Small presentational helpers ------------------------------------------------

const Pill = ({ children, tone = "default" }: { children: React.ReactNode; tone?: "default" | "muted" | "success" | "warn" }) => {
    const tones: Record<string, string> = {
        default: "bg-primary/10 text-primary",
        muted: "bg-muted text-muted-foreground",
        success: "bg-emerald-500/10 text-emerald-600",
        warn: "bg-amber-500/10 text-amber-600",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
            {children}
        </span>
    );
};

const InfoRow = ({ label, value }: { label: string; value: any }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
        <div className="flex items-start justify-between gap-3 py-1 text-xs">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="font-mono text-right break-all">{String(value)}</span>
        </div>
    );
};

const Section = ({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) => (
    <div className="rounded-xl border bg-background p-3">
        <div className="flex items-center gap-2 mb-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-semibold">{title}</h4>
        </div>
        <div className="space-y-0.5">{children}</div>
    </div>
);

const prettyRelative = (iso?: string | null) => {
    if (!iso) return "—";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return "—"; }
};

// ----------------------------------------------------------------------------

const User = () => {
    const [users, setUsers] = useState<any[]>([]);
    const [viewModal, setViewModal] = useState(false);
    const [userDetail, setUserDetail] = useState<any>();
    const [deviceData, setDeviceData] = useState<{ devices: any[]; sessions: any[] }>({ devices: [], sessions: [] });
    const [stats, setStats] = useState<any>(null);
    const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);

    useEffect(() => {
        getUsers();
    }, []);

    const getUsers = async () => {
        try {
            setLoading(true);
            const res = await getAllUsers();
            setUsers(res.data.data);
        } catch {
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    const handleView = async (data: any) => {
        setViewModal(true);
        setViewLoading(true);
        setDeviceData({ devices: [], sessions: [] });
        setStats(null);
        try {
            const [detailRes, devicesRes, statsRes] = await Promise.all([
                getuserById({ id: data.id }),
                getUserDevices(data.id).catch(() => ({ data: { data: { devices: [], sessions: [] } } })),
                getUserStats(data.id).catch(() => ({ data: { data: null } })),
            ]);
            setUserDetail(detailRes.data.data);
            setDeviceData(devicesRes.data?.data ?? { devices: [], sessions: [] });
            setStats(statsRes.data?.data ?? null);
            setOpenAccordionIndex(null);
        } finally {
            setViewLoading(false);
        }
    };

    const toggleAccordion = (index: number) => {
        setOpenAccordionIndex(openAccordionIndex === index ? null : index);
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Users</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        End users who book turfs. Columns show the most recent device + activity signal. Open a row for the full fingerprint, session log and booking behaviour.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => getUsers()}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>Device</TableHead>
                                    <TableHead>App</TableHead>
                                    <TableHead>Locale</TableHead>
                                    <TableHead>Last active</TableHead>
                                    <TableHead className="text-right">Sessions</TableHead>
                                    <TableHead className="text-right w-20">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center text-muted-foreground py-14">
                                            No users found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users?.map((item: any, index: number) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-full border overflow-hidden bg-muted/50 shrink-0">
                                                        <img
                                                            src={item.image}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/wood-blog-placeholder.jpg"; }}
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium truncate max-w-[14ch]">{item.name || "—"}</div>
                                                        <div className="text-xs text-muted-foreground truncate max-w-[18ch]">{item.email || "—"}</div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm tabular-nums">{item.phone}</TableCell>
                                            <TableCell className="text-sm">
                                                {item.last_device_brand || item.last_device_model ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-medium truncate max-w-[14ch]">
                                                            {[item.last_device_brand, item.last_device_model].filter(Boolean).join(" ")}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {[item.last_platform, item.last_os_version].filter(Boolean).join(" ")}
                                                        </span>
                                                    </div>
                                                ) : <span className="text-muted-foreground text-xs">—</span>}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {item.last_app_version ? (
                                                    <Pill tone="muted">v{item.last_app_version}{item.last_app_build ? ` (${item.last_app_build})` : ""}</Pill>
                                                ) : <span className="text-muted-foreground">—</span>}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                <div>{item.last_language || item.last_locale || "—"}</div>
                                                <div className="text-muted-foreground">{item.last_timezone || ""}</div>
                                            </TableCell>
                                            <TableCell className="text-xs">{prettyRelative(item.last_active_at)}</TableCell>
                                            <TableCell className="text-right text-sm tabular-nums">{item.total_sessions ?? 0}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => handleView(item)}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* View user dialog */}
            <Dialog open={viewModal} onOpenChange={setViewModal}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>User details</DialogTitle>
                        <DialogDescription>Profile, device fingerprint, activity and booking history.</DialogDescription>
                    </DialogHeader>
                    {viewLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        </div>
                    ) : userDetail ? (
                        <div className="space-y-4">
                            {/* User profile */}
                            <div className="flex items-center gap-4 p-3 rounded-xl bg-muted/30">
                                <img
                                    src={userDetail?.user?.image}
                                    alt=""
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/wood-blog-placeholder.jpg"; }}
                                    className="h-16 w-16 rounded-full object-cover border-2 shrink-0"
                                />
                                <div className="min-w-0">
                                    <h2 className="text-sm font-bold">{userDetail?.user?.name || "—"}</h2>
                                    <p className="text-sm text-muted-foreground truncate">
                                        {userDetail?.user?.email || "—"} · {userDetail?.user?.phone}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {userDetail?.user?.address && <Pill tone="muted">{userDetail.user.address}</Pill>}
                                        {userDetail?.user?.first_seen_at && <Pill tone="muted">joined {format(new Date(userDetail.user.first_seen_at), "dd MMM yyyy")}</Pill>}
                                        {userDetail?.user?.last_active_at && <Pill tone="success">active {prettyRelative(userDetail.user.last_active_at)}</Pill>}
                                    </div>
                                </div>
                            </div>

                            {/* Behavioural insights */}
                            {stats && (
                                <Section icon={Activity} title="Booking behaviour">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        <div className="p-2 rounded-lg bg-muted/30">
                                            <p className="text-xs text-muted-foreground">Total bookings</p>
                                            <p className="text-lg font-semibold tabular-nums">{stats.total_bookings_count ?? 0}</p>
                                        </div>
                                        <div className="p-2 rounded-lg bg-muted/30">
                                            <p className="text-xs text-muted-foreground">Avg amount</p>
                                            <p className="text-lg font-semibold tabular-nums">₹{Number(stats.avg_booking_amount ?? 0).toFixed(0)}</p>
                                        </div>
                                        <div className="p-2 rounded-lg bg-muted/30">
                                            <p className="text-xs text-muted-foreground">Fav time</p>
                                            <p className="text-lg font-semibold capitalize">{stats.preferred_time_slot ?? "—"}</p>
                                        </div>
                                        <div className="p-2 rounded-lg bg-muted/30">
                                            <p className="text-xs text-muted-foreground">Fav weekday</p>
                                            <p className="text-lg font-semibold">{stats.preferred_weekday ?? "—"}</p>
                                        </div>
                                        <div className="p-2 rounded-lg bg-muted/30">
                                            <p className="text-xs text-muted-foreground">Fav sport</p>
                                            <p className="text-lg font-semibold capitalize">{stats.preferred_sport ?? "—"}</p>
                                        </div>
                                        <div className="p-2 rounded-lg bg-muted/30">
                                            <p className="text-xs text-muted-foreground">Fav box</p>
                                            <p className="text-lg font-semibold tabular-nums">#{stats.preferred_box_id ?? "—"}</p>
                                        </div>
                                    </div>
                                </Section>
                            )}

                            {/* Current fingerprint summary from users table */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Section icon={Smartphone} title="Current device">
                                    <InfoRow label="Brand/model" value={[userDetail?.user?.last_device_brand, userDetail?.user?.last_device_model].filter(Boolean).join(" ") || "—"} />
                                    <InfoRow label="Platform" value={userDetail?.user?.last_platform} />
                                    <InfoRow label="OS" value={[userDetail?.user?.last_os_name, userDetail?.user?.last_os_version].filter(Boolean).join(" ")} />
                                    <InfoRow label="App version" value={userDetail?.user?.last_app_version} />
                                    <InfoRow label="App build" value={userDetail?.user?.last_app_build} />
                                    <InfoRow label="Device ID" value={userDetail?.user?.last_device_id} />
                                </Section>

                                <Section icon={Globe} title="Locale & location">
                                    <InfoRow label="Language" value={userDetail?.user?.last_language} />
                                    <InfoRow label="Locale" value={userDetail?.user?.last_locale} />
                                    <InfoRow label="Region" value={userDetail?.user?.last_region} />
                                    <InfoRow label="Timezone" value={userDetail?.user?.last_timezone} />
                                    <InfoRow label="GPS lat" value={userDetail?.user?.latitude} />
                                    <InfoRow label="GPS lng" value={userDetail?.user?.longitude} />
                                    <InfoRow label="Address" value={userDetail?.user?.address} />
                                </Section>

                                <Section icon={Wifi} title="Network">
                                    <InfoRow label="IP address" value={userDetail?.user?.last_ip} />
                                    <InfoRow label="Network" value={userDetail?.user?.last_network_type} />
                                    <InfoRow label="Carrier" value={userDetail?.user?.last_carrier} />
                                    <InfoRow label="User agent" value={userDetail?.user?.last_user_agent} />
                                </Section>

                                <Section icon={Clock} title="Activity">
                                    <InfoRow label="First seen" value={userDetail?.user?.first_seen_at && format(new Date(userDetail.user.first_seen_at), "dd MMM yyyy HH:mm")} />
                                    <InfoRow label="Last active" value={userDetail?.user?.last_active_at && format(new Date(userDetail.user.last_active_at), "dd MMM yyyy HH:mm")} />
                                    <InfoRow label="Logins" value={userDetail?.user?.total_sessions} />
                                    <InfoRow label="App opens" value={userDetail?.user?.total_app_opens} />
                                </Section>
                            </div>

                            {/* All devices this user has used */}
                            {deviceData.devices.length > 0 && (
                                <div>
                                    <h3 className="text-base font-semibold mb-2">Devices ({deviceData.devices.length})</h3>
                                    <div className="overflow-x-auto rounded-lg border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Device</TableHead>
                                                    <TableHead>OS</TableHead>
                                                    <TableHead>App</TableHead>
                                                    <TableHead>Locale</TableHead>
                                                    <TableHead>IP</TableHead>
                                                    <TableHead className="text-right">Sessions</TableHead>
                                                    <TableHead>Last seen</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {deviceData.devices.map((d: any) => (
                                                    <TableRow key={d.id}>
                                                        <TableCell className="text-xs">
                                                            <div className="font-medium">{[d.device_brand, d.device_model].filter(Boolean).join(" ") || "—"}</div>
                                                            <div className="text-muted-foreground">{d.device_name}</div>
                                                        </TableCell>
                                                        <TableCell className="text-xs">{[d.os_name, d.os_version].filter(Boolean).join(" ")}</TableCell>
                                                        <TableCell className="text-xs">{d.app_version}{d.app_build_version ? ` (${d.app_build_version})` : ""}</TableCell>
                                                        <TableCell className="text-xs">
                                                            <div>{d.language || d.locale || "—"}</div>
                                                            <div className="text-muted-foreground">{d.timezone}</div>
                                                        </TableCell>
                                                        <TableCell className="text-xs font-mono">{d.ip_address || "—"}</TableCell>
                                                        <TableCell className="text-right text-xs tabular-nums">{d.session_count}</TableCell>
                                                        <TableCell className="text-xs">{prettyRelative(d.last_seen_at)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {/* Recent sessions */}
                            {deviceData.sessions.length > 0 && (
                                <div>
                                    <h3 className="text-base font-semibold mb-2">Recent sessions</h3>
                                    <div className="overflow-x-auto rounded-lg border max-h-64 overflow-y-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>When</TableHead>
                                                    <TableHead>Kind</TableHead>
                                                    <TableHead>Platform</TableHead>
                                                    <TableHead>App</TableHead>
                                                    <TableHead>IP</TableHead>
                                                    <TableHead>Network</TableHead>
                                                    <TableHead>Location</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {deviceData.sessions.map((s: any) => (
                                                    <TableRow key={s.id}>
                                                        <TableCell className="text-xs whitespace-nowrap">
                                                            {s.started_at ? format(new Date(s.started_at), "dd MMM HH:mm") : "—"}
                                                        </TableCell>
                                                        <TableCell className="text-xs">
                                                            <Pill tone={s.kind === "login" ? "success" : "muted"}>{s.kind}</Pill>
                                                        </TableCell>
                                                        <TableCell className="text-xs">{s.platform} {s.os_version}</TableCell>
                                                        <TableCell className="text-xs">{s.app_version}</TableCell>
                                                        <TableCell className="text-xs font-mono">{s.ip_address || "—"}</TableCell>
                                                        <TableCell className="text-xs">{s.network_type || "—"}</TableCell>
                                                        <TableCell className="text-xs">
                                                            {s.latitude && s.longitude ? (
                                                                <span className="inline-flex items-center gap-1">
                                                                    <MapPin className="h-3 w-3" />
                                                                    {Number(s.latitude).toFixed(2)}, {Number(s.longitude).toFixed(2)}
                                                                </span>
                                                            ) : "—"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {/* Bookings */}
                            <div>
                                <h3 className="text-base font-semibold mb-2">Bookings</h3>
                                {userDetail?.bookings?.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">No bookings available.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {userDetail?.bookings?.map((data: any, index: number) => {
                                            const formattedDate = format(new Date(data?.created_at), 'dd-MM-yyyy');
                                            const isOpen = openAccordionIndex === index;
                                            return (
                                                <div key={index} className="border rounded-xl overflow-hidden">
                                                    {/* Accordion header */}
                                                    <button
                                                        type="button"
                                                        className="w-full text-left p-3 bg-muted/20 hover:bg-muted/40 transition-colors flex items-center gap-3"
                                                        onClick={() => toggleAccordion(index)}
                                                    >
                                                        <img
                                                            src={data?.partner.logo}
                                                            alt=""
                                                            className="h-12 w-12 rounded-full object-cover border-2 shrink-0"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between">
                                                                <h4 className="text-sm font-bold truncate">{data?.partner.name}</h4>
                                                                <span className="text-xs text-muted-foreground ml-2 shrink-0">{formattedDate}</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground truncate">{data?.partner.email} · {data?.partner.phone}</p>
                                                            <p className="text-xs text-muted-foreground truncate"><span className="font-medium">Address:</span> {data?.partner.address}</p>
                                                        </div>
                                                        {isOpen ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
                                                    </button>

                                                    {/* Accordion content */}
                                                    {isOpen && (
                                                        <div className="p-3 border-t bg-background">
                                                            <h5 className="text-xs font-semibold mb-2">Slots:</h5>
                                                            <div className="overflow-x-auto rounded-lg border">
                                                                <Table>
                                                                    <TableHeader>
                                                                        <TableRow>
                                                                            <TableHead className="w-10">#</TableHead>
                                                                            <TableHead>Box name</TableHead>
                                                                            <TableHead>Slot timing</TableHead>
                                                                            <TableHead className="text-right">Price</TableHead>
                                                                        </TableRow>
                                                                    </TableHeader>
                                                                    <TableBody>
                                                                        {data.slots.map((item: any, slotIndex: number) => (
                                                                            <TableRow key={slotIndex}>
                                                                                <TableCell>{slotIndex + 1}</TableCell>
                                                                                <TableCell className="text-sm">{data?.box.name}</TableCell>
                                                                                <TableCell className="text-sm font-mono">{item?.from_time} – {item?.to_time}</TableCell>
                                                                                <TableCell className="text-sm text-right tabular-nums">
                                                                                    ₹{item?.price === "" ? 0 : item?.price}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </div>
                                                            <div className="flex justify-between border-t pt-2 mt-3">
                                                                <p className="text-sm font-semibold">Total:</p>
                                                                <p className="text-sm font-semibold tabular-nums">
                                                                    ₹{data.slots.reduce((total: number, item: any) => total + (isNaN(parseFloat(item?.price)) ? 0 : parseFloat(item?.price)), 0).toFixed(2)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default User;
